import { call, put, select } from 'redux-saga/effects';
import { hasPermissionsSaga } from 'features/permissions/sagas/hasPermissionsSaga';
import { hasDynamicPermissionsSaga } from 'features/permissions/sagas/hasDynamicPermissionsSaga';
import { PermissionTypes } from 'features/permissions/types';
import {
  broadcastPermissionGranted,
  broadcastPermissionRevoked,
  dynamicBroadcastPermissionsReset,
  signalingRoleChanged,
} from 'features/permissions/actions';
import { User } from 'features/users/types';
import { selectIsAudioOnlyRoom } from 'features/room/roomSlice';

interface BroadcastPermissionState {
  oldRoleAllowed: boolean;
  newRoleAllowed: boolean;
  userHadDynamic: boolean;
}

function* checkBroadcastPermissionsSaga(oldRole: string, newRole: string, user: User) {
  const oldRoleAllowed: boolean = yield call(
    hasPermissionsSaga,
    [PermissionTypes.broadcastAudio, PermissionTypes.broadcastVideo],
    {
      mode: 'any',
      role: oldRole,
    }
  );

  const newRoleAllowed: boolean = yield call(
    hasPermissionsSaga,
    [PermissionTypes.broadcastAudio, PermissionTypes.broadcastVideo],
    {
      mode: 'any',
      role: newRole,
    }
  );

  const userHadDynamic: boolean = yield call(
    hasDynamicPermissionsSaga,
    [PermissionTypes.broadcastAudio, PermissionTypes.broadcastVideo],
    {
      mode: 'any',
      targetUser: user,
    }
  );

  return {
    oldRoleAllowed,
    newRoleAllowed,
    userHadDynamic,
  };
}

export function* handleRoleChangeBroadcastPermissionsSaga(
  payload: ReturnType<typeof signalingRoleChanged>['payload'],
  user: User
) {
  const { role, id } = payload;
  const { role: oldRole } = user;

  const permissionState: BroadcastPermissionState = yield call(
    checkBroadcastPermissionsSaga,
    oldRole,
    role,
    user
  );

  const hadBroadcastAccess = permissionState.oldRoleAllowed || permissionState.userHadDynamic;
  const hasBroadcastAccess = permissionState.newRoleAllowed;

  const permissionsGranted = !hadBroadcastAccess && hasBroadcastAccess;
  const permissionsRevoked = hadBroadcastAccess && !hasBroadcastAccess;

  yield put(dynamicBroadcastPermissionsReset({ id }));

  if (permissionsGranted) {
    const isAudioOnlyRoom: boolean = yield select(selectIsAudioOnlyRoom);

    yield put(
      broadcastPermissionGranted({
        id: payload.id,
        initiator: payload.initiator,
        audio: true,
        video: !isAudioOnlyRoom,
      })
    );
    return;
  }

  if (permissionsRevoked) {
    yield put(broadcastPermissionRevoked(payload));
  }
}
